/**
 * How a call into a person's connected app reads in the chat timeline.
 *
 * eve reaches Gmail, Drive, Slack and the rest through one workspace tool,
 * `plugins_execute_action`, whose arguments name the connector and the action
 * (`GMAIL_FETCH_EMAILS`). Presented as that tool, every such call is the same
 * "Run app action" row with a plug for an icon and a truncated JSON blob for a
 * detail. Presented as what it did, a Gmail search reads "Searching emails"
 * beside the Gmail mark, with the query as its hint and the full request one
 * click away.
 *
 * The identity comes from the tool's own input (`toolData`, carried by the
 * adapter), never from output text: a page the agent fetched cannot dress a
 * call up as something else. Common actions have curated wording; anything
 * else is read off the slug's verb, and a slug with no recognisable verb
 * falls back to "Using Gmail", still under the right mark.
 *
 * @module components/chat/connectorToolPresentation
 */
import { connectorActionWording, connectorDisplayName } from "@amoreg/shared/connectorActions";
export { connectorDisplayName } from "@amoreg/shared/connectorActions";
import { bareToolName } from "@amoreg/shared/toolName";

import type { WorkLogEntry, WorkLogToolLifecycleStatus } from "../../session-logic";
import { formatToolInputLines, toolInputLines } from "./toolInputText";

export interface ConnectorToolCall {
  /** The connector id the Plugins page knows (`gmail`, `googledrive`). */
  readonly connectorId: string;
  /** The connector's product name, for wording that has to name it. */
  readonly connectorName: string;
  readonly accountScope: "personal" | "workspace" | null;
  readonly toolSlug: string | null;
  readonly version: string | null;
  /** The action's arguments, parsed when the tool passed them as JSON text. */
  readonly arguments: unknown;
  /** Infinitive phrase, used for "Failed to …" / "Declined to …". */
  readonly title: string;
  readonly running: string;
  readonly completed: string;
  /** The one argument worth reading in the row (a query, a recipient). */
  readonly hint: string | null;
  /**
   * The row predates the adapter carrying the call's input, so what is known
   * was read off the one-line summary and the arguments may be cut short.
   */
  readonly truncatedSummary: string | null;
}

/** The generic argument a row can show: the first of these that is short text. */
const HINT_KEYS: ReadonlyArray<string> = [
  "query",
  "q",
  "search_query",
  "recipient_email",
  "to",
  "subject",
  "title",
  "summary",
  "name",
  "file_name",
  "filename",
  "url",
  "channel",
];

const HINT_MAX_LENGTH = 120;

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const optionalString = (value: unknown): string | null =>
  typeof value === "string" && value.trim().length > 0 ? value.trim() : null;

/** `plugins_execute_action` passes its arguments as JSON text; read them back. */
function parseArguments(value: unknown): unknown {
  if (typeof value !== "string") return value;
  const text = value.trim();
  if (!text.startsWith("{") && !text.startsWith("[")) return value;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    // Malformed input stays as the text the tool actually received.
    return value;
  }
}

function hintFrom(args: unknown, keys: ReadonlyArray<string>): string | null {
  const record = asRecord(args);
  if (!record) return null;
  for (const key of keys) {
    const value = optionalString(record[key]);
    if (value) {
      const singleLine = value.replace(/\s+/g, " ");
      return singleLine.length > HINT_MAX_LENGTH
        ? `${singleLine.slice(0, HINT_MAX_LENGTH - 1)}…`
        : singleLine;
    }
  }
  return null;
}

/**
 * The connector call behind a work-log entry, or null when the entry is not
 * one (a different tool, a saved row from before the adapter carried inputs,
 * an input too large to travel).
 */
export function resolveConnectorToolCall(
  entry: Pick<WorkLogEntry, "toolName" | "itemType" | "toolData" | "detail">,
): ConnectorToolCall | null {
  if (entry.itemType !== "dynamic_tool_call" || !entry.toolName) return null;
  const tool = bareToolName(entry.toolName).toLowerCase();
  if (tool !== "plugins_execute_action" && tool !== "plugins_search_tools") return null;
  const input = asRecord(entry.toolData);
  if (!input) return resolveLegacyCall(tool, entry.detail);
  // A row resolves its call several times per render (heading, hint, icon,
  // body); the input object is stable across renders, so resolve it once.
  const cached = resolved.get(input);
  if (cached && cached.tool === tool) return cached.call;
  const call = resolveCall(tool, input, null);
  resolved.set(input, { tool, call });
  return call;
}

const resolved = new WeakMap<object, { tool: string; call: ConnectorToolCall | null }>();
const legacyResolved = new Map<string, ConnectorToolCall | null>();
const LEGACY_CACHE_LIMIT = 500;

/**
 * Rows from before the adapter carried the call's input have only the
 * one-line summary: the first 200 characters of the input as JSON. Its
 * leading keys — scope, connector, action, version — are almost always
 * intact, so the row can still wear the connector's mark and the action's
 * wording; the arguments are taken when the summary parses whole and shown
 * as the cut-short text when it does not.
 */
function resolveLegacyCall(tool: string, detail: string | undefined): ConnectorToolCall | null {
  const summary = detail?.trim();
  if (!summary || !summary.startsWith("{")) return null;
  // The two plugin tools read the same summary differently, so the key names both.
  const cacheKey = `${tool}\n${summary}`;
  const cached = legacyResolved.get(cacheKey);
  if (cached !== undefined) return cached;
  let input: Record<string, unknown> | null = null;
  let truncated: string | null = null;
  try {
    input = asRecord(JSON.parse(summary));
  } catch {
    const field = (key: string): string | undefined =>
      new RegExp(`"${key}"\\s*:\\s*"([^"\\\\]*)"`).exec(summary)?.[1];
    const connectorId = field("connectorId");
    if (connectorId) {
      input = {
        connectorId,
        accountScope: field("accountScope"),
        toolSlug: field("toolSlug"),
        version: field("version"),
      };
      truncated = summary;
    }
  }
  const call = input === null ? null : resolveCall(tool, input, truncated);
  if (legacyResolved.size >= LEGACY_CACHE_LIMIT) legacyResolved.clear();
  legacyResolved.set(cacheKey, call);
  return call;
}

function resolveCall(
  tool: string,
  input: Record<string, unknown>,
  truncatedSummary: string | null,
): ConnectorToolCall | null {
  const connectorId = optionalString(input.connectorId)?.toLowerCase() ?? null;
  if (!connectorId) return null;
  const connectorName = connectorDisplayName(connectorId);
  const scope = input.accountScope;
  const accountScope = scope === "personal" || scope === "workspace" ? scope : null;

  if (tool === "plugins_search_tools") {
    const query = optionalString(input.query);
    return {
      connectorId,
      connectorName,
      accountScope,
      toolSlug: null,
      version: null,
      arguments: null,
      title: `find ${connectorName} actions`,
      running: `Finding ${connectorName} actions`,
      completed: `Found ${connectorName} actions`,
      hint: query,
      truncatedSummary,
    };
  }

  const toolSlug = optionalString(input.toolSlug);
  const args = input.arguments === undefined ? null : parseArguments(input.arguments);
  const wording = connectorActionWording(toolSlug ?? "", connectorId);
  return {
    connectorId,
    connectorName,
    accountScope,
    toolSlug,
    version: optionalString(input.version),
    arguments: args,
    title: wording.title,
    running: wording.running,
    completed: wording.completed,
    hint: hintFrom(args, wording.hintKeys ?? HINT_KEYS),
    truncatedSummary,
  };
}

export function connectorToolHeading(
  call: ConnectorToolCall,
  status: WorkLogToolLifecycleStatus | undefined,
): string {
  const capitalised = call.title.charAt(0).toUpperCase() + call.title.slice(1);
  switch (status) {
    case "inProgress":
      return call.running;
    case "completed":
      return call.completed;
    case "failed":
      return `Failed to ${call.title}`;
    case "declined":
      return `Declined to ${call.title}`;
    case "stopped":
      return `Stopped: ${capitalised}`;
    default:
      return capitalised;
  }
}

/**
 * The full request for the row's disclosure, as text: which app and account,
 * which action, and each argument on its own line ("Query: in:drafts").
 */
export function connectorToolDetails(call: ConnectorToolCall): string {
  const lines = [
    `App: ${call.connectorName}${
      call.accountScope === "workspace"
        ? " (shared workspace integration)"
        : call.accountScope === "personal"
          ? " (your account)"
          : ""
    }`,
  ];
  if (call.toolSlug) lines.push(`Action: ${call.toolSlug}`);
  if (call.toolSlug === null && call.hint) lines.push(`Looking for: ${call.hint}`);
  const argumentLines = toolInputLines(call.arguments);
  if (argumentLines.length > 0) lines.push(formatToolInputLines(argumentLines));
  if (call.truncatedSummary !== null) {
    lines.push("\nRecorded before this chat kept the full request.");
  }
  return lines.join("\n");
}
